import React from 'react'
import PropTypes from 'prop-types'
import { Row, Col } from 'reactstrap'
import { Grid, Paper, Box, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Tooltip, Typography } from '@material-ui/core'
import { withStyles } from '@material-ui/core/styles'
import CircularProgress from '@material-ui/core/CircularProgress'
import TableSortLabel from '@material-ui/core/TableSortLabel'
import moment from 'moment'

import { TableFormats } from '../TableHelpers'
import { TRENDING_TOPICS_INSTAGRAM, TRENDING_TOPICS_TIKTOK, TRENDING_TOPICS_TWITTER, TRENDING_TOPICS_YOUTUBE } from '../../constants/commons'
import '../calendar/CalendarComponent.scss'
import './TrendingComponent.scss'

const StyledTableCell = withStyles(() => ({
  head: {
    background: '#F4F5F6',
    height: 48,
    color: '#808184'
  }
}))(TableCell)

const NGRAM_SIZE = 3
const NGRAM_MATCH_THRESHOLD = 0.9

/**
 * Normalizes a string for search: lowercases, substitutes letter-like symbols
 * (e.g. $ -> s, & -> and), then strips remaining non-alphanumeric chars.
 * Enables "ASAP" to match "A$AP Rocky", "rock" to match "Rock & Roll", etc.
 */
function normalizeForSearch (str) {
  if (str == null || typeof str !== 'string') return ''
  let s = str.toLowerCase()
  // Substitute symbols that commonly stand for letters/words so "asap" matches "A$AP"
  s = s.replace(/\$/g, 's')
  s = s.replace(/&/g, ' and ')
  s = s.replace(/[^a-z0-9\s]/gi, '')
  s = s.replace(/\s+/g, ' ').trim()
  return s
}

/**
 * Returns a Set of n-grams (character substrings of length n) from a normalized string.
 * Used for n-gram similarity search.
 */
function getNgrams (str, n = NGRAM_SIZE) {
  const normalized = typeof str === 'string' ? str : normalizeForSearch(str)
  const grams = new Set()
  if (normalized.length < n) return grams
  for (let i = 0; i <= normalized.length - n; i++) {
    grams.add(normalized.slice(i, i + n))
  }
  return grams
}

/**
 * Returns n-gram similarity: fraction of search n-grams that appear in the title.
 * 1.0 = all search n-grams found in title; 0.9+ = 90% match threshold.
 */
function ngramSimilarity (titleStr, searchStr, n = NGRAM_SIZE) {
  const titleGrams = getNgrams(titleStr, n)
  const searchGrams = getNgrams(searchStr, n)
  if (searchGrams.size === 0) return 0
  let overlap = 0
  for (const g of searchGrams) {
    if (titleGrams.has(g)) overlap += 1
  }
  return overlap / searchGrams.size
}

export default class TrendingTableComponent extends React.Component {
  static propTypes = {
    section: PropTypes.object,
    isFetching: PropTypes.bool,
    selectedDay: PropTypes.object,
    globalSearchText: PropTypes.string
  }

  state = {
    screenWidth: window.innerWidth,
    page: 0,
    rowsPerPage: 10,
    order: 'desc',
    orderBy: 'trending_count',
    maxLength: 20
  }

  componentDidUpdate (prevProps) {
    if (prevProps.selectedDay !== this.props.selectedDay) {
      this.setState({ page: 0 })
    }
    if (prevProps.globalSearchText !== this.props.globalSearchText) {
      this.setState({ page: 0 })
    }
  }

  handleChangePage = (event, newPage) => {
    this.setState({ page: newPage })
  }

  handleChangeRowsPerPage = (event) => {
    this.setState({
      rowsPerPage: parseInt(event.target.value, 10),
      page: 0
    })
  }

  descendingComparator = (a, b, orderBy) => {
    let aValue = a[orderBy]
    let bValue = b[orderBy]

    // Convert to number for trending_count and engagement_count to ensure numeric sorting
    if (orderBy === 'trending_count' || orderBy === 'engagement_count') {
      aValue = Number(aValue) || 0
      bValue = Number(bValue) || 0
    }

    if (bValue < aValue) {
      return -1
    }
    if (bValue > aValue) {
      return 1
    }
    return 0
  }

  getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => this.descendingComparator(a, b, orderBy)
      : (a, b) => -this.descendingComparator(a, b, orderBy)
  }

  sortData = (array, comparator) => {
    const stabilizedThis = array.map((el, index) => [el, index])
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0])
      if (order !== 0) return order
      return a[1] - b[1]
    })
    return stabilizedThis.map((el) => el[0])
  }

  createSortHandler = (property) => {
    const isAsc = this.state.orderBy === property && this.state.order === 'asc'
    this.setState({
      order: isAsc ? 'desc' : 'asc',
      orderBy: property,
      page: 0
    })
  }

  truncateText = (text) => {
    if (!text) return ''
    return text.length > this.state.maxLength ? text.substring(0, this.state.maxLength) + '...' : text
  }

  formatTitle = (section, row) => {
    if (!row) return ''
    if (section.id === TRENDING_TOPICS_YOUTUBE) {
      const fullTitle = row.videotitle || ''
      const truncatedTitle = this.truncateText(fullTitle)
      return (
        <Tooltip title={fullTitle} arrow>
          <a href={'https://www.youtube.com/watch?v=' + row.videoid} target="_blank" rel="noopener noreferrer" className='trendLink'>
            {truncatedTitle}
          </a>
        </Tooltip>
        )
    } else if (section.id === TRENDING_TOPICS_TWITTER) {
      const fullName = row.name || ''
      const truncatedName = this.truncateText(fullName)
      return (
        <Tooltip title={fullName} arrow>
          <a href={`https://x.com/search?q="${row.name}"&src=trend_click&vertical=trends`} target="_blank" rel="noopener noreferrer" className='trendLink'>
            {truncatedName}
          </a>
        </Tooltip>
        )
    } else if (section.id === TRENDING_TOPICS_INSTAGRAM) {
      const fullName = row.name || ''
      const truncatedName = this.truncateText(fullName)
      return (
        <Tooltip title={fullName} arrow>
          <a href={`https://www.instagram.com/explore/search/keyword/?q=${row.name}`} target="_blank" rel="noopener noreferrer" className='trendLink'>
            {truncatedName}
          </a>
        </Tooltip>
        )
    } else if (section.id === TRENDING_TOPICS_TIKTOK) {
      const fullName = row.name || ''
      const truncatedName = this.truncateText(fullName)
      return (
        <Tooltip title={fullName} arrow>
          <a href={`https://www.tiktok.com/tag/${row.name}`} target="_blank" rel="noopener noreferrer" className='trendLink'>
            {truncatedName}
          </a>
        </Tooltip>
        )
    }
    return this.truncateText(row.name || '', 40)
  }

  formatEngagements = (section, count) => {
    if (count && (section.id === TRENDING_TOPICS_YOUTUBE || section.id === TRENDING_TOPICS_INSTAGRAM || section.id === TRENDING_TOPICS_TIKTOK)) {
      return TableFormats.trackerFormatKM(count)
    } else if (section.id === TRENDING_TOPICS_TWITTER) {
      const numCount = Number(count)
      if (isNaN(numCount) || numCount <= 0) {
        return 'n/a'
      }
      return TableFormats.trackerFormatKM(count)
    }
  }

  formatTrendingCount = (section, count) => {
    if (section.id === TRENDING_TOPICS_YOUTUBE || section.id === TRENDING_TOPICS_TWITTER) {
      return `${count} (hrs)`
    } else {
      return `${count} (d)`
    }
  }

  formatLastUpdatedDate = (dateString) => {
    try {
      const date = moment.tz(
        dateString,
        'YYYY-MM-DD HH:mm:ss.SSS z',
        'US/Eastern'
      )

      if (!date.isValid()) return ''

      return date
        .tz('America/New_York')
        .format('MMM DD, YYYY | h:mm A z')
    } catch (e) {
      return ''
    }
  }

  render () {
    const { section, isFetching, selectedDay, globalSearchText } = this.props
    let rows = section.rows || []
    const { page, rowsPerPage, order, orderBy } = this.state

    // Check if selectedDay is today
    const isToday = selectedDay && moment(selectedDay).isSame(moment(), 'day')

    const searchText = (globalSearchText || '').trim()
    if (searchText.length > 0) {
      const searchValue = normalizeForSearch(searchText)
      const searchLower = searchText.toLowerCase()
      rows = rows.filter(row => {
        if (section.id === TRENDING_TOPICS_YOUTUBE) {
          const rawTitle = row.videotitle ?? row.video_title ?? ''
          const normalizedTitle = normalizeForSearch(rawTitle)
          const ngramMatch = ngramSimilarity(normalizedTitle, searchValue) >= NGRAM_MATCH_THRESHOLD
          return (
            normalizedTitle.indexOf(searchValue) > -1 ||
            (rawTitle && rawTitle.toLowerCase().indexOf(searchLower) > -1) ||
            ngramMatch
          )
        } else {
          const rawName = row.name || ''
          const normalizedName = normalizeForSearch(rawName)
          const ngramMatch = ngramSimilarity(normalizedName, searchValue) >= NGRAM_MATCH_THRESHOLD
          return (
            normalizedName.indexOf(searchValue) > -1 ||
            (rawName && rawName.toLowerCase().indexOf(searchLower) > -1) ||
            ngramMatch
          )
        }
      })
    }

    // Sort rows
    rows = this.sortData(rows, this.getComparator(order, orderBy))
    const sm = (this.state.screenWidth >= 904) ? 6 : 12
    return (
      <Grid item xl={3} lg={3} md={6} sm={sm} xs={12} key={section.id} className="trendCardGrid">
        <Paper className="trendCard">
          <Box>
            <Box className="cardTitle">{section.title}</Box>
            <Box className="cardSubTitle">{section.description}</Box>
          </Box>
          { isFetching ? (
            <div className='loadingProgress'>
              <CircularProgress />
            </div>
          ) : (
            <TableContainer className="trendTable">
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <StyledTableCell className="dateHeader trendTable__title">
                      <div className='headerTitle'>Title</div>
                    </StyledTableCell>
                    <StyledTableCell className="trendTable__hoursHeader" sortDirection={this.state.orderBy === 'engagement_count' ? this.state.order : false}>
                      <TableSortLabel
                        active={this.state.orderBy === 'engagement_count'}
                        direction={this.state.orderBy === 'engagement_count' ? this.state.order : 'asc'}
                        onClick={() => this.createSortHandler('engagement_count')}
                        hideSortIcon={false}
                      >
                        <div className='headerTitle'>Engagement</div>
                      </TableSortLabel>
                    </StyledTableCell>
                    <StyledTableCell className="trendTable__hoursHeader" sortDirection={this.state.orderBy === 'trending_count' ? this.state.order : false}>
                      <TableSortLabel
                        active={this.state.orderBy === 'trending_count'}
                        direction={this.state.orderBy === 'trending_count' ? this.state.order : 'asc'}
                        onClick={() => this.createSortHandler('trending_count')}
                        hideSortIcon={false}
                      >
                        <div className='headerTitle'>{section.id === TRENDING_TOPICS_TWITTER || section.id === TRENDING_TOPICS_YOUTUBE ? 'Hours' : 'Days'}</div>
                      </TableSortLabel>
                    </StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} align="center" className="noDataCell">Data processing, check back soon</TableCell>
                    </TableRow>
                  )}
                  {rows.length > 0 && rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => (
                    <TableRow key={`${section.id}_${index}`}>
                      <TableCell className="trendTable__topic">
                        {this.formatTitle(section, row)}
                      </TableCell>
                      <TableCell className="trendTable__hours">
                        {isToday ? '-' : this.formatEngagements(section, row.engagement_count)}
                      </TableCell>
                      <TableCell className="trendTable__hours">
                        {isToday ? '-' : this.formatTrendingCount(section, row.trending_count)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              { rows.length > 0 &&
                <TablePagination
                  component="div"
                  rowsPerPageOptions={[]}
                  labelRowsPerPage=""
                  labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
                  count={rows.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onChangePage={this.handleChangePage}
                  onChangeRowsPerPage={this.handleChangeRowsPerPage}
                />
            }
            </TableContainer>
          )}
        </Paper>
        <Row>
          <Col className="dateCol">
            <Box className="dateTxt">
              <Typography>
                Data last updated {this.formatLastUpdatedDate(section.trendingDate)}
              </Typography>
            </Box>
          </Col>
        </Row>
      </Grid>
    )
  }
}